import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Edit, Plus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CityGroup {
  id: string;
  primary_city: string;
  display_cities: string[];
  county: string;
  region?: string;
  created_at: string;
  updated_at: string;
}

export const CityGroupsManager = () => {
  const [cityGroups, setCityGroups] = useState<CityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingGroup, setEditingGroup] = useState<CityGroup | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    primary_city: "",
    display_cities: ["", "", ""],
    county: "",
    region: ""
  });

  useEffect(() => {
    loadCityGroups();
  }, []);

  const loadCityGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('city_groups')
        .select('*')
        .order('county', { ascending: true })
        .order('primary_city', { ascending: true });

      if (error) throw error;
      setCityGroups(data || []);
    } catch (error) {
      console.error('Error loading city groups:', error);
      toast({
        title: "Error",
        description: "Failed to load city groups",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (group: CityGroup) => {
    setEditingGroup(group);
    setFormData({
      primary_city: group.primary_city,
      display_cities: [...group.display_cities, "", ""].slice(0, 3), // Ensure 3 slots
      county: group.county,
      region: group.region || ""
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setFormData({
      primary_city: "",
      display_cities: ["", "", ""],
      county: "",
      region: ""
    });
    setIsCreating(true);
  };

  const handleSave = async () => {
    try {
      const filteredDisplayCities = formData.display_cities.filter(city => city.trim() !== "");
      
      if (!formData.primary_city || !formData.county || filteredDisplayCities.length === 0) {
        toast({
          title: "Validation Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      const cityGroupData = {
        primary_city: formData.primary_city,
        display_cities: filteredDisplayCities,
        county: formData.county,
        region: formData.region || null
      };

      if (editingGroup) {
        const { error } = await supabase
          .from('city_groups')
          .update(cityGroupData)
          .eq('id', editingGroup.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "City group updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('city_groups')
          .insert([cityGroupData]);

        if (error) throw error;
        toast({
          title: "Success",
          description: "City group created successfully",
        });
      }

      setEditingGroup(null);
      setIsCreating(false);
      loadCityGroups();
    } catch (error) {
      console.error('Error saving city group:', error);
      toast({
        title: "Error",
        description: "Failed to save city group",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (groupId: string) => {
    if (!confirm("Are you sure you want to delete this city group?")) return;

    try {
      const { error } = await supabase
        .from('city_groups')
        .delete()
        .eq('id', groupId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "City group deleted successfully",
      });
      loadCityGroups();
    } catch (error) {
      console.error('Error deleting city group:', error);
      toast({
        title: "Error",
        description: "Failed to delete city group",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setEditingGroup(null);
    setIsCreating(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading city groups...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">City Groups Manager</h2>
          <p className="text-muted-foreground">Manage location-based project display settings</p>
        </div>
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add City Group
        </Button>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingGroup) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingGroup ? 'Edit City Group' : 'Create City Group'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="primary_city">Primary City *</Label>
                <Input
                  id="primary_city"
                  value={formData.primary_city}
                  onChange={(e) => setFormData({ ...formData, primary_city: e.target.value })}
                  placeholder="e.g., East Hampton"
                />
              </div>
              <div>
                <Label htmlFor="county">County *</Label>
                <Select value={formData.county} onValueChange={(value) => setFormData({ ...formData, county: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select county" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nassau">Nassau</SelectItem>
                    <SelectItem value="Suffolk">Suffolk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="region">Region (Optional)</Label>
              <Input
                id="region"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                placeholder="e.g., East End, North Shore"
              />
            </div>

            <div>
              <Label>Display Cities (Project Locations)</Label>
              <div className="space-y-2">
                {formData.display_cities.map((city, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-16">City {index + 1}:</span>
                    <Input
                      value={city}
                      onChange={(e) => {
                        const newCities = [...formData.display_cities];
                        newCities[index] = e.target.value;
                        setFormData({ ...formData, display_cities: newCities });
                      }}
                      placeholder={`e.g., ${index === 0 ? 'East Hampton' : index === 1 ? 'Amagansett' : 'Southampton'}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                These cities will be shown as project locations when someone visits the primary city page
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave}>
                {editingGroup ? 'Update' : 'Create'} City Group
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* City Groups List */}
      <div className="grid gap-4">
        {cityGroups.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">No city groups found.</p>
            </CardContent>
          </Card>
        ) : (
          cityGroups.map((group) => (
            <Card key={group.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{group.primary_city}</h3>
                      <Badge variant="secondary">{group.county}</Badge>
                      {group.region && <Badge variant="outline">{group.region}</Badge>}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Project locations shown:</p>
                      <div className="flex flex-wrap gap-1">
                        {group.display_cities.map((city, index) => (
                          <Badge key={index} variant="default">
                            {index + 1}. {city}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(group)}
                      className="flex items-center gap-1"
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(group.id)}
                      className="flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};